import { Injectable } from '@angular/core';
import { GeoComplyStatus, GeoResponse, StoredGeoResponse } from 'src/app/interfaces/geo-interface';
import { CustomerModel } from 'src/app/models/customer-model';
import { GeoLocationPreRequestService } from './geo-location-pre-request.service';
import { GeoLocationSDKInitService } from './geo-location-sdkinit.service';
import { ConfigService } from '../config/config.service';
import { GeoLocationErrorHandlerService } from './geo-location-error-handler.service';
import { WebservicesService } from '../webservices/webservices.service';
import { UserSessionService } from '../user-session/user-session.service';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { Navigator } from "src/app/services/routing/navigator";
import { GeoLocationSchedulerService } from './geo-location-scheduler.service';
import { map, take } from 'rxjs/operators';
import { EventsService } from '../event/event.service';
import { LoadingController } from '@ionic/angular';
import { UtilsService } from '../utils/utils.service';
import { ScriptService } from '../script/script.service';
import { ReplaySubject, Subscription } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const coerceInt = (a: string | number) => ("string" === typeof a ? parseInt(a) : a);

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})

export class GeoLocationControllerService {

  private isAuthenticated: boolean;
  private customer: CustomerModel;
  public customer$: ReplaySubject<CustomerModel> = new ReplaySubject(1);
  private isGeoLocationInProgress: boolean;
  private isGeoLocationProcessed: boolean;
  private geoCheckTimeoutId: any;
  private useNDBSSolution: boolean;
  private loadingIndicator: HTMLIonLoadingElement | null = null;
  private showGeoDialogRunningCheck: boolean;
  private userSubscr: Subscription;

  constructor(
    private geoLocationPreRequestService: GeoLocationPreRequestService,
    private geoLocationSDKInitService: GeoLocationSDKInitService,
    private geoLocationErrorHandlerService: GeoLocationErrorHandlerService,
    private geoLocationSchedulerService: GeoLocationSchedulerService,
    private scriptService: ScriptService,
    private webservices: WebservicesService,
    private eventService: EventsService,
    private utilsService: UtilsService,
    private navigator: Navigator,
    private loadingController: LoadingController,
    private userSessionService: UserSessionService,
    private configService: ConfigService
  ) {
    //Need proper/reliable solution for customer with it's authentication status
    this.useNDBSSolution = this.configService.isFeatureON( this.configService.getConfig(), "FEATURE_GEOCOMPLY_USENODOWNLOAD", undefined);
    this.showGeoDialogRunningCheck = this.configService.getConfig().FEATURE_SHOW_GEO_RUNNING_DIALOG === '1' ? true : false;
    this.customer = null;
    this.userSubscr = this.userSessionService.getCustomer().subscribe((customer: CustomerModel) => {
      if( undefined === customer)
        return;
      if( !customer || !customer.customerId) 
        return;
      if((!this.customer || customer.customerId != this.customer.customerId)) {
        this.customer = customer;
        this.isAuthenticated = true;
        this.userSessionService.sessionToken$
          .pipe(
            map(token => !!token),
            untilDestroyed(this)
          )
          .subscribe(() => {
            this.isAuthenticated = this.userSessionService.isAuthenticated();
          });
        this.customer$.next( customer);
      }
    })
    this.userSessionService.refreshCustomer();
  }

  /**
    * Executes the geo-location process by validating the user's geo-location status.
    * - First, checks if the user is authenticated. If not, rejects the promise with an authentication error.
    * - If a geo-location check is already in progress, it displays a loader and rejects the promise to avoid duplication.
    * - Initiates a geo-location validity check using the `GeoLocationSchedulerService`.
    * - If the geo-location is valid, it hides the loader and returns a success response.
    * - If the geo-location is invalid, it determines whether it's a first-time check or a periodic check to be run.
    * - The method handles showing and hiding loaders during the process, as well as handling errors during the geo-location validation.
    * @returns {Promise<GeoResponse>} Resolves with a `GeoResponse` object indicating the geo-location status:
    * - If geo-location is valid: { is_allowed: true, address: undefined }
    * - If geo-location is invalid: returns the result of running either `runFirstGeoLocation` or `runPeriodicGeoLocation` based on the status of `isGeoLocationProcessed`.
    * @throws {Promise<string>} Rejects with an error message if:
    * - User is not authenticated.
    * - Geo-location check is already in progress.
    * - There is an error during geo-location validation.
  **/
  public async executeGeoLocationProcess(): Promise<GeoResponse> {
    if (!this.isAuthenticated) return Promise.reject('User is not authenticated');
    console.log(this.isGeoLocationProcessed)

    //Add listen for constant GeoComply Error to show it on UI
    if (this.isGeoLocationInProgress) {
      this.eventService.publish('event:show:debounce:loader');
      console.log('GeoLocation check is already in progress...');
      return Promise.reject('GeoLocation check is already in progress.');
    }

    try {
      // Wait for geo-location validity check
      this.isGeoLocationInProgress = true;
      await this.showGeoLocationIndicatior();
      const data = await this.geoLocationSchedulerService.isGeoLocationValid();

      if (data) {
        this.isGeoLocationInProgress = false;
        await this.hideGeoLocationIndicatior();
        return {
          is_allowed: true,
          address: undefined
        } as GeoResponse;
      } else {
        // If geo-location is not valid, decide which geo-location function to run
        let result;
        if (this.isGeoLocationProcessed) {
          console.log('************run periodic check************');
          result = await this.runPeriodicGeoLocation();
        } else {
          console.log('************run first geolocation check************');
          result = await this.runFirstGeoLocation();
        }
        this.isGeoLocationInProgress = false;
        await this.hideGeoLocationIndicatior();
        return result;
      }
    } catch (error) {
      console.log('************error in isGeoLocationValid Check************');
      this.isGeoLocationInProgress = false;
      await this.hideGeoLocationIndicatior();
      return Promise.reject('Error in geo-location validation');
    }

  }
  
  /**
    * Executes the first-time geo-location process, including pre-checks, SDK initialization, and geo-location requests.
    * This method performs a sequence of actions to verify the user's geo-location for the first time:
    * - It performs a geo-location pre-check to validate that the necessary conditions are met.
    * - It initializes the GeoComply SDK to handle geo-location requests.
    * - It requests a geo-location license from backend and pass to GeoComply SDK.
    * - It finally requests the geo-location data, indicating it's the first geo-location check.
    * If any of these steps fail, the method handles the error and returns a standardized geo-location error response.
    * @returns {Promise<GeoResponse>} Resolves with the geo-location data if all steps succeed. 
    * If any step fails, it returns a geo-location error response.
    * @throws {Promise<GeoResponse>} Rejects with a geo-location error if any of the steps fail.
  **/
  private async runFirstGeoLocation(): Promise<GeoResponse> {
    try {
      // Step 1: Perform GeoLocation PreCheck
      const preCheckResponse = await this.geoLocationPreRequestService.performGeoLocationPreCheck();
      if (!preCheckResponse) {
        return this.handleGeoLocationError();
      }
      // Step 2: Initialize GeoComply SDK
      const initGeoComply = await this.geoLocationSDKInitService.initializeGeoComply();
      if (!initGeoComply) {
        return this.handleGeoLocationError();
      }
      // Step 3: Request GeoComply License
      const licenseData = await this.geoLocationSDKInitService.requestGeoComplyLicense(this.configService.getClientApp());
      if (!licenseData) {
        return this.handleGeoLocationError();
      }
      // Step 4: Request GeoComply Location
      const geoLocationData = await this.requestGeoLocation('FIRST_GEOLOCATION');
      return geoLocationData;
    } catch (error) {
      // Step 5: Handle Error
      return this.handleGeoLocationError(error);
    }
  }

  /**
    * Executes the periodic geo-location process to check the user's current geo-location status.
    * This method runs when the geo-location is already processed but needs to be periodically checked (e.g., re-validation based on geolocate_in field or network change detection action).
    * It requests the geo-location data for periodic validation.
    * @returns {Promise<GeoResponse>} Resolves with the geo-location data for periodic check.
    * @throws {Promise<GeoResponse>} Rejects with a geo-location error if the periodic check fails.
  **/
  private async runPeriodicGeoLocation(): Promise<GeoResponse> {
    try {
      // Request GeoComply Location
      console.log('************run periodic chek************')
      const geoLocationData = await this.requestGeoLocation('PERIODIC');
      console.log('************run periodic chek response************', geoLocationData)
      return geoLocationData;
    } catch (error) {
      console.warn('************GeoLocation process failed for Periodic check************', error);
      return this.handleGeoLocationError(error);
    }
  }

  private async requestGeoLocation(reason: string): Promise<GeoResponse> {
    //test failed license case to trigger new license and re-run the geo from that point without displayin on UI.
    const geoLocationData = await this.getGeoComplyLocation(this.geoLocationSDKInitService.getCertificate(this.configService.getClientApp()).license, reason);
    console.log(geoLocationData);
    return geoLocationData;
  }

  private async getGeoComplyLocation(licenseCertificate: string, reason: string): Promise<GeoResponse> {
    const platformInfo = this.utilsService.getPlatformInfo();
    console.log(platformInfo)
    switch (true) {
      case platformInfo.isMobileApp:
        return this.getGeoComplyLocationForMobileApp(licenseCertificate, reason);

      case platformInfo.isMobileWeb && this.useNDBSSolution:
        return this.getGeoComplyLocationNDBS(licenseCertificate, reason);

      case platformInfo.isMobileWeb && !this.useNDBSSolution:
        return this.getGeoComplyOobeeSolution(licenseCertificate, reason);

      case platformInfo.isDesktopWeb && this.useNDBSSolution:
        return this.getGeoComplyLocationNDBS(licenseCertificate, reason);

      case platformInfo.isDesktopWeb && !this.useNDBSSolution:
        return this.getGeoComplyLocationPLC(licenseCertificate, reason);

      default:
        return this.getGeoComplyLocationPLC(licenseCertificate, reason);
    }
  }

  private async getGeoComplyLocationNDBS(licenseCertificate: string, reason: string): Promise<GeoResponse> {
    return new Promise<GeoResponse>((resolve, reject) => {
      const geoComplyClient = this.geoLocationSDKInitService.getGeoComplyWindowClient();
      geoComplyClient.setLicense(licenseCertificate);
      geoComplyClient.setUserId(this.customer.customerId);
      geoComplyClient.setReason(reason);
      //sets some usefull information which can be logged to GeoComply data record lie reason code, seesion ID
      geoComplyClient.request();

      let isEventHandled = false;

      const successHandler = async (encryptedResponse: string) => {
        if (isEventHandled) {
          console.log('************GeoComply success event already handled.************');
          return;
        }
        try {
          console.log("************Request GeoComply location************");
          const data: GeoResponse = await this.getGeoLocationResponse(encryptedResponse);
          console.log("************Geo Response************", data);
          if (data?.is_allowed) {
            resolve(data);
          } else {
            reject(data);
          }
        } catch (error) {
          reject(new Error('Error processing geo location response: ' + error.message));
        } finally {
          isEventHandled = true;
          removeListeners();
        }
      };
      const failureHandler = (code: BigInteger, message: string) => {
        if (isEventHandled) {
          console.log('************GeoComply event already handled, skipping failure handler************');
          return;
        }
        console.log(`************ GeoComply error: ${message} with code: ${code}`);
        reject(new Error(`GeoComply failure with code: ${code}`));
        removeListeners();
      };
      geoComplyClient.events.on('success', successHandler);
      geoComplyClient.events.on('failed', failureHandler);
      const removeListeners = () => {
        geoComplyClient.events.off('success', successHandler);
        geoComplyClient.events.off('failed', failureHandler);
      };
    });
  }

  private async getGeoComplyLocationPLC(licenseCertificate: string, reason: string): Promise<GeoResponse> {
    //PLC logic starts here
    console.log('************PLC is setting up...please wait...************');
    const geoComplyClient = this.geoLocationSDKInitService.getGeoComplyWindowClient();
    return new Promise<GeoResponse>((resolve, reject) => {
      let isGeoRequestTriggered = false;
      if (geoComplyClient.isConnected()) {
        if (!isGeoRequestTriggered) {
          isGeoRequestTriggered = true;
          geoComplyClient.setLicense(licenseCertificate);
          geoComplyClient.setGeolocationReason(reason);
          geoComplyClient.setUserId(this.customer.customerId);

          geoComplyClient.off('geolocation');
          geoComplyClient.on('geolocation', async (encryptedGeolocationResult) => {
            console.log('************GeoComply geolocation result received - send to backend server to process the result************');
            try {
              const data: GeoResponse = await this.getGeoLocationResponse(encryptedGeolocationResult);
              if (data?.is_allowed) {
                resolve(data);
              } else {
                reject(data);
              }
            } catch (error) {
              reject(new Error('Error processing geo location response: ' + error.message));
            }
          });
          //Handle Error on periodic check if user uninstalled the PLC 
          //Not for this scope - Work In Progress
          geoComplyClient.on('error', async (errorCode: any, errorMessage: string) => {
            console.log('************Error in PLC from controller ************' + errorCode + " : " + errorMessage);
            switch(errorCode) {
              case 606:
              case 608:
                //fix proper way to re-genearte license and flow
                //Not for this scope - will be fixed in next release
                //this.updateLicense().then((data) =>  this.getGeoComplyLocationPLC(data, reason));
                break;
              default:
                console.log('************Unhandled Error Codr received in GeoLocation************', errorCode);
                const geoComplyError: GeoComplyStatus = await this.geoLocationErrorHandlerService.getGeoComplyError();
                console.log('************GeoComply Error Received in Controller************', geoComplyError);
                this.geoLocationSchedulerService.killConnection();
                break;
            }
          })
          console.log('************Requesting geolocation************');
          geoComplyClient.requestGeolocation();
        } else {
          console.log('************GeoComply geolocation request already triggered. Skipping************');
        }
      } else {
        console.warn('************Error: PLC is not connected************');
        reject(new Error('PLC is not connected'));
      }
    });
  }

  private getGeoComplyOobeeSolution(licenseCertificate: string, reason: string): Promise<GeoResponse> {
    return new Promise<GeoResponse>((resolve, reject) => {
      console.log('************Oobee Solution is setting up...please wait...************');
      const geoComplyClient = this.geoLocationSDKInitService.getGeoComplyWindowClient();
      console.log(geoComplyClient)
      geoComplyClient.state.data.license = licenseCertificate;
      let isStopped = false;  
      geoComplyClient.events
        .on(geoComplyClient.EVENTS.HINT, function (hint) {
          console.log(hint);
        })
      // Cleanup function
      const cleanup = () => {
        console.log('Cleaning up GeoComply client...');
        //geoComplyClient.off('data', middleCallback);
        geoComplyClient.stopUpdating();
        isStopped = true;
      };
      // Geolocation options
      const geolocateOptions = {
        userId: this.customer.customerId,
        reason: reason
      };
      // Middle Callback
      //Need to handle more case and have to find solution to close the connection to oobee - no information foud in their SDK Document
      const middleCallback = async (data: any) => {
        if (isStopped) return;
        switch (data.type) {
          case 'LOG':
            console.log(data.message);
            break;
          // There is a reason field inside the INTERACTION object for clients can
          // detect if they want to use their own message as below:
          // 'app_required': when need to open app to geolocate.
          // 'tlb_require': when need to open TLB to geolocate.
          // 'location_required': when Location permission is not granted.
          // 'precise_location_required': when Precise Location permission is not granted.
          // 'notification_required': when Notification on IOS permission is not granted.  
          case 'INTERACTION':
            console.log(data);
            console.log(data.message);
            switch (data.reason) {
              case 'app_required':
                console.log('App required to complete geolocation. Please open the GeoGuard Location Validator to geolocate');
                this.geoLocationErrorHandlerService.setGeoComplyError({
                  error_code: 113,
                  error_message: 'Please open the GeoGuard Location Validator to geolocate',
                  troubleshooter_message: 'Please open the GeoGuard Location Validator to geolocate',
                  error_details: '',
                  geolocate_in: 0,
                  hint: undefined,
                });
                cleanup();
                reject();
                return;
              case 'location_required':
                console.log('Please allow \"GeoGuard Location Validator\"  to access your location');
                this.geoLocationErrorHandlerService.setGeoComplyError({
                  error_code: 611,
                  error_message: 'Please allow \"GeoGuard Location Validator\"  to access your location',
                  troubleshooter_message: 'Please allow \"GeoGuard Location Validator\"  to access your location',
                  error_details: '',
                  geolocate_in: 0,
                  hint: undefined,
                });
                cleanup();
                reject();
                return;
              case 'precise_location_required':
                console.log('Please allow \"GeoGuard Location Validator\"  to access your Precise location');
                this.geoLocationErrorHandlerService.setGeoComplyError({
                  error_code: 637,
                  error_message: 'Please allow \"GeoGuard Location Validator\"  to access your Precise location',
                  troubleshooter_message: 'Please allow \"GeoGuard Location Validator\"  to access your Precise location',
                  error_details: '',
                  geolocate_in: 0,
                  hint: undefined,
                });
                cleanup();
                reject();
                return;
              default:
                console.log(`Unhandled interaction reason: ${data.reason}`);
                break;
            }
            break;
          default:
            console.log(`Unhandled ${data.type}: ${data.message}`);
        }
      };
      // Final Callback
      const finalCallback = async (err: any, data: any) => {
        if (isStopped) return;
        if (err) {
          console.error('Geolocate failed with error code:', err);
          cleanup();
          reject(new Error(`Geolocation failed: ${err.error_message}`));
          return;
        } else {
          console.log('Geolocate succeeded with data:', data);
          try {
            const response: GeoResponse = await this.getGeoLocationResponse(data);
            if (response?.is_allowed) {
              cleanup();
              resolve(response);
            } else {
              console.error('Geolocation not allowed:', response);
              cleanup();
              reject(response);
            }
          } catch (error: any) {
            console.error('Error processing geo location response:', error);
            cleanup();
            reject(new Error('Error processing geo location response: ' + error.message));
          }
        }
      };
      // Initiating geolocation for oobee
      geoComplyClient.geolocate(geolocateOptions, middleCallback, finalCallback);
    });
  }

  private getGeoComplyLocationForMobileApp(licenseCertificate: string, reason: string): Promise<GeoResponse> {
    return new Promise<GeoResponse>((resolve, reject) => {
      console.log('************GeoComply Mobile SDK is setting up...please wait...************');
      const geoComplyClient = this.geoLocationSDKInitService.getGeoComplyWindowClient();
      console.log(geoComplyClient);
      // Request runtime permissions
      this.requestRuntimePermissions(
        (success: any) => {
          console.info('GeoComply:: requestRuntimePermissions: ', success);
          geoComplyClient.requestGeolocation(
            // Success callback
            async (data: any) => {
              try {
                // Using arrow function preserves the context of "this"
                const response: GeoResponse = await this.getGeoLocationResponse(data);
                if (response?.is_allowed) {
                  resolve(response);
                } else {
                  console.error('Geolocation not allowed:', response);
                  reject(response);
                }
              } catch (error: any) {
                console.error('Error processing geo location response:', error);
                reject(new Error('Error processing geo location response: ' + error.message));
              }
            },
            // Message callback
            (message: string) => {
              console.info('GeoComply message callback: ' + JSON.stringify(message));
              //Not For this Scope - Work In Progress to handle SDK Error 
              //cancel current geo and stop any other service for cleanup. Need more information from SDK to try and confirm.
              //handle 637 for Precise Location is turned off
              /*this.geoLocationErrorHandlerService.setGeoComplyError({
                error_code: 637,
                error_message: 'Indicates that the app has turned off precise accuracy location in Settings app',
                troubleshooter_message: 'Precise location is disabled/off.',
                error_details: '',
                geolocate_in: 0,
                hint: undefined,
              });*/
              //cancel current geo and stop any other service for cleanup. Need more information from SDK to try and confirm.
              //return this.handleGeoLocationError();
            },
            // Log callback
            (logMessage: string) => {
              console.info('GeoComply Log:: ' + logMessage);
            },
            // Geolocation options
            [
              {
                user_id: this.customer.customerId.toString(),
                reason: reason,
                license: licenseCertificate
              }
            ]
          );
        },
        // Error callback for requestRuntimePermissions
        (error: any) => {
          const errorMessage = "Can not do geolocation: " + error;
          console.error('GeoComply Error: ' + errorMessage);
          this.geoLocationErrorHandlerService.setGeoComplyError({
            error_code: 113,
            error_message: 'Please open the GeoGuard Location Validator to geolocate',
            troubleshooter_message: 'Please open the GeoGuard Location Validator to geolocate',
            error_details: '',
            geolocate_in: 0,
            hint: undefined,
          });
          // Reject the promise on error
          reject(new Error(errorMessage));
        }
      );
    });
  }

  private getGeoLocationResponse(data): Promise<any> {
    console.log('************getGeoLocationResponse************');
    return this.webservices.post('geoComply/data', {
      customer_id: this.customer.customerId.toString(),
      session_token: this.userSessionService.getSessionToken(),
      enc_data: encodeURIComponent(data),
      clientapp: this.configService.getClientApp()
    }).toPromise().then((response: PlatformResponse<any>) => {
      console.log('************' + JSON.stringify(response) + '************');
      if (response.payload.error_code == 0 || response.payload.is_allowed == 'true') {
        console.log(
          "%cGeolocation: PLAYER IS ALLOWED.",
          "color: green; font-weight: bold"
        );
        const geolocate_in = coerceInt(response.payload.geolocate_in);
        const bufferTime = this.geoLocationSchedulerService.getBufferTime(geolocate_in);
        const geoLocationRecheckTimer: any = geolocate_in - bufferTime;
        //const geoLocationRecheckTimer: any = 180
        console.log(`************Next GeoLocation is set to run in ${geoLocationRecheckTimer} seconds ************`);
        const passedGeoLocationResponse: StoredGeoResponse = {
          geoResponse: {
            is_allowed: true,
            address: undefined,
          },
          expiry_ts: new Date().getTime() + geoLocationRecheckTimer * 1000 // this field should be dynamic based on server reposnse
        }
        //location is already checked
        this.isGeoLocationProcessed = true;
        this.setGeoLocationResponse(passedGeoLocationResponse);
        this.scheduleNextGeolocation(geoLocationRecheckTimer);
        const customerData = {
          customerID: this.customer.customerId,
          sessionToken: this.userSessionService.getSessionToken()
        }
        this.geoLocationSchedulerService.startIPCheck(customerData);
        this.geoLocationSchedulerService.ipChange$.pipe(take(1)).subscribe(() => {
          console.log('************GeoLocation: IP change detected!************');
          this.scheduleNextGeolocation(0);
        });
        return passedGeoLocationResponse.geoResponse;
      } else {
        console.log(
          "%cGeolocation: PLAYER IS DENIED.",
          "color: red; font-weight: bold"
        );
        this.geoLocationSchedulerService.stopIPCheck();
        const failedGeoLocationResponse: StoredGeoResponse = {
          geoResponse: {
            is_allowed: false,
            address: undefined,
          },
          expiry_ts: 0
        }
        //should we store failed Geo Response ? don't think - we need to ? Please confirm.
        this.geoLocationErrorHandlerService.setGeoComplyError({
          error_code: response.payload.error_code,
          error_message: response.payload?.error_message,
          troubleshooter_message: response.payload?.troubleshooter_message,
          error_details: response.payload?.error_details,
          geolocate_in: 0,
          hint: undefined,
        });
        return failedGeoLocationResponse;
      }
    }).catch((e: any) => {
      console.warn('************ EXCEPTION ************: ', e);
    });
  }

  private async handleGeoLocationError(error?: any): Promise<GeoResponse> {
    console.warn('************GeoLocation process failed for First Geolocation check************', error);
    const geoComplyError: GeoComplyStatus = await this.geoLocationErrorHandlerService.getGeoComplyError();
    console.log('************GeoComply Error Received in Controller************');
    this.navigator.navigateTo('GeoLocationViewComponent', {}, { geoComplyError: geoComplyError });
    return Promise.resolve({
      is_allowed: false,
      address: undefined,
      error: geoComplyError
    } as GeoResponse);
  }

  //Make this method public as Android App needs to be required to request app location permission at app launch
  public requestRuntimePermissions(onSuccess, onError) {
    const geoComplyClient = this.geoLocationSDKInitService.getGeoComplyWindowClient();
    geoComplyClient.requestRuntimePermissions(
      function (success) {
        if (typeof (onSuccess) === 'function') {
          onSuccess(success);
        }
      },
      function (error) {
        if (typeof (onError) === 'function') {
          onError(error);
        }
      },
      function (logMessage) {
        console.log(logMessage);
      }, [{
        "request_precise_location_permission": true
      }]
    );
  }

  private setGeoLocationResponse(geoResponse: StoredGeoResponse): Promise<void> {
    this.geoLocationSchedulerService.storeGeoLocationResponse(geoResponse);
    return;
  }

  private scheduleNextGeolocation(geolocate_s: number) {
    if (this.geoCheckTimeoutId) {
      clearTimeout(this.geoCheckTimeoutId);
      this.geoCheckTimeoutId = 0;
    }
    this.geoCheckTimeoutId = setTimeout(() => {
      this.reCheckGeoLocation(); 
    }, geolocate_s * 1000);
  }

  private reCheckGeoLocation() {
    if (this.customer && this.isAuthenticated) {
      //location is already checked
      this.isGeoLocationProcessed = true;
      //clear/invalidate stored GeoResponse 
      const failedGeoLocationResponse: StoredGeoResponse = {
        geoResponse: {
          is_allowed: false,
          address: undefined,
        },
        expiry_ts: 0
      }
      this.setGeoLocationResponse(failedGeoLocationResponse);
      this.executeGeoLocationProcess().then((geoResponse) => {
        if (geoResponse.is_allowed) {
          return;
        } else {
          console.log(geoResponse)
        }
      }).catch((error) => {
        // Handle any error that occurs during the geo-location check
        console.warn('Error during geo-location check:', error);
      });
    }
  }

  public async updateGeoLocationCachedResponseFromServer(data: StoredGeoResponse): Promise<void> {
    try {
      this.setGeoLocationResponse(data);
      // Step 1: Re-Perform GeoLocation PreCheck
      const preCheckResponse = await this.geoLocationPreRequestService.performGeoLocationPreCheck();
      if (!preCheckResponse) {
        this.handleGeoLocationError();
      }
      // Step 2: Re-Initialize GeoComply SDK
      const initGeoComply = await this.geoLocationSDKInitService.initializeGeoComply();
      if (!initGeoComply) {
        this.handleGeoLocationError();
      }
      // Step 3: Re-Request GeoComply License
      const licenseData = await this.geoLocationSDKInitService.requestGeoComplyLicense(this.configService.getClientApp());
      if (!licenseData) {
        this.handleGeoLocationError();
      }
      this.isGeoLocationProcessed = true;
      const customerData = {
        customerID: this.customer.customerId,
        sessionToken: this.userSessionService.getSessionToken()
      }
      this.geoLocationSchedulerService.startIPCheck(customerData);
      this.scheduleNextGeolocation(data.expiryTime / 1000);
      this.geoLocationSchedulerService.ipChange$.pipe(take(1)).subscribe(() => {
        console.log('GeoLocation: IP change detected!');
        this.scheduleNextGeolocation(0);
      });
    } catch (error) {
      console.warn('GeoLocation process failed for First Geolocation check:', error);
      const geoComplyError: GeoComplyStatus = await this.geoLocationErrorHandlerService.getGeoComplyError();
      console.log('GeoComply Error Received in Controller:');
      this.navigator.navigateTo('GeoLocationViewComponent', {}, { geoComplyError: geoComplyError });
    }
  }

  /**
   * Shows the loading indicator
   */
  async showGeoLocationIndicatior(): Promise<void> {
    if (this.showGeoDialogRunningCheck) {
      if (!this.loadingIndicator) {
        this.loadingIndicator = await this.loadingController.create({
          message: 'Please wait geolocation is running...',
        });
        await this.loadingIndicator.present();
      }
    }
  }

  /**
   * Hides the loading indicator
   */
  async hideGeoLocationIndicatior(): Promise<void> {
    if (this.showGeoDialogRunningCheck) {
      if (this.loadingIndicator) {
        await this.loadingIndicator.dismiss();
        this.loadingIndicator = null;
      }
    }  
  }

  cleanupGeoLocation(): void {
    //Cleanup timer, cleanup SDK calls
    if (this.geoCheckTimeoutId) {
      clearTimeout(this.geoCheckTimeoutId);
    }
    const geoComplyClient = this.geoLocationSDKInitService.getGeoComplyWindowClient();
    this.geoLocationSchedulerService.killConnection();
  }

  clearCustomer(): void {
    this.userSubscr?.unsubscribe();
    this.customer = null;
  }

  /**
   * 
   * Not For this scope - Work In Progress
   */
  private async updateLicense() {
    const licenseData = await this.geoLocationSDKInitService.requestGeoComplyLicense(this.configService.getClientApp());
    if (!licenseData) {
      return this.handleGeoLocationError();
    } else {
      return licenseData
    }
  }

  /**
   * 
   * Not For this scope - Work In Progress
   */
  //This method seems can't initiated when Geo Is Started - can't use it. Need to think different approach in the logic.
  private checkIfGeoLocationInProgress(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const geoComplyClient = this.geoLocationSDKInitService.getGeoComplyWindowClient();
      const platformInfo = this.utilsService.getPlatformInfo();
      if (platformInfo.isMobileApp) {
        geoComplyClient.isGeolocationInProgress(
          (isInProgress) => {
            console.log(`Geolocation In Progress: ${isInProgress}`);
            resolve(isInProgress); // Return the boolean value correctly
          },
          (errorMessage) => {
            console.error(`GeoComply Error: ${errorMessage}`);
            resolve(false); // Return false if an error occurs
          }
        );
      } else {
        resolve(this.isGeoLocationInProgress); // Default to false for non-mobile app scenarios
      }
    });
  }
}
